import * as React from "react";
import { css } from "@emotion/core";
import { Icon, Colors, Classes, Collapse } from "@blueprintjs/core";
import { DraggingPosition } from "react-complex-tree";
import { ROOT_DROPZONE_ID } from "../constants";
import { cx } from "../utils";

export const createTreeRenderers = (
  caretClickRef: React.MutableRefObject<boolean>,
  onHoverArticle?: (id: number) => void
) => {
  return {
    renderTreeContainer: (props: any) => (
      <div className={cx(Classes.TREE)}>
        <ul
          className={cx(Classes.TREE_ROOT, Classes.TREE_NODE_LIST)}
          {...props.containerProps}
        >
          {props.children}
        </ul>
      </div>
    ),

    renderItemsContainer: (props: any) => (
      <ul
        css={css`
          list-style: none;
          margin: 0;
          padding-left: 0;
        `}
        {...props.containerProps}
      >
        {props.children}
      </ul>
    ),

    renderItem: (props: any) => {
      const isRootDropZone =
        props.item.index === ROOT_DROPZONE_ID ||
        props.item.data?.isRootDropZone;
      if (isRootDropZone) {
        const isDropTarget = !!props.context.isDraggingOver;
        return (
          <li
            {...(props.context.itemContainerWithoutChildrenProps as any)}
            data-root-dropzone
            css={css`
              margin-top: 0;
              height: 0;
              position: relative;
              overflow: visible;
            `}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              css={css`
                position: absolute;
                left: 0;
                right: 0;
                top: -8px;
                height: 120px;
                border-radius: 8px;
                background-color: ${isDropTarget
                  ? "rgba(29, 123, 229, 0.12)"
                  : "transparent"};
                transition: background-color 120ms ease;
                cursor: ${isDropTarget ? "copy" : "default"};
              `}
            ></div>
          </li>
        );
      }

      const interactiveElementProps =
        props.context.interactiveElementProps || {};
      const { onClick: _onTitleClick, ...restInteractiveProps } =
        interactiveElementProps;

      const hasChildren =
        (props.item as any).hasChildren ?? props.item.isFolder === true;
      const showArrow = props.item.isFolder && hasChildren;
      const isSelected = !!props.context.isSelected;
      const isFocused = !!props.context.isFocused;
      const isDropTarget = !!props.context.isDraggingOver;
      const selectThisItem = () => {
        if (props.context.selectItem) {
          props.context.selectItem();
        }
      };

      const handleHover = () => {
        if (!onHoverArticle) return;
        if (
          props.item.index === "root" ||
          props.item.index === ROOT_DROPZONE_ID ||
          props.item.data?.isRootDropZone
        ) {
          return;
        }
        const id = Number(props.item.index);
        if (!Number.isNaN(id)) {
          onHoverArticle(id);
        }
      };

      return (
        <li
          {...(props.context.itemContainerWithChildrenProps as any)}
          data-has-children={hasChildren}
          css={css`
            margin-bottom: 0.25rem;
          `}
          onMouseEnter={handleHover}
          onClick={(e) => {
            e.stopPropagation();
            selectThisItem();
          }}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 0.5rem;
              padding: 0 0 0 ${props.depth * 1.5}rem;
              border-radius: 8px;
              transition: background-color 120ms ease, color 120ms ease;
              background-color: ${isDropTarget
                ? "rgba(29, 123, 229, 0.12)"
                : isSelected
                ? "rgba(255, 255, 255, 0.12)"
                : isFocused
                ? "rgba(255, 255, 255, 0.06)"
                : "transparent"};
              ${isDropTarget
                ? `
                    box-shadow: 0 0 0 2px ${Colors.BLUE2};
                  `
                : ""}
              ${!isSelected
                ? `
                    &:hover {
                      background-color: rgba(255, 255, 255, 0.04);
                    }
                  `
                : ""}
              ${showArrow
                ? `&:hover [data-role="file"] {
                opacity: 0;
              }
              &:hover [data-role="caret"] {
                opacity: 1;
              }
              &:hover [data-role="arrow"] {
                background-color: rgba(255, 255, 255, 0.12);
                color: #ffffff;
              }`
                : ""}
            `}
            {...(props.context.itemContainerWithoutChildrenProps as any)}
            {...(restInteractiveProps as any)}
          >
            {showArrow ? (
              props.arrow
            ) : (
              <span
                data-has-children="false"
                aria-hidden
                css={css`
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  width: 20px;
                  height: 20px;
                  margin: 6px 0 6px 6px;
                  border-radius: 8px;
                  gap: 4px;
                  color: #c5cbd3;
                  cursor: pointer;
                  transition: background-color 120ms ease, color 120ms ease;

                  [data-role="file"] {
                    opacity: 0.8;
                    display: inline-flex;
                    align-items: center;
                  }
                `}
                onClick={(e) => {
                  e.stopPropagation();
                  selectThisItem();
                }}
              >
                <Icon icon="document" data-role="file" />
              </span>
            )}

            <span
              css={css`
                cursor: pointer;
                flex: 1;
                color: ${isSelected ? "var(--neutral-2)" : "var(--neutral-4)"};
                transition: color 120ms ease;
              `}
            >
              {props.title}
            </span>
          </div>
          <div
            className={cx(Classes.COLLAPSE)}
            css={css`
              ${props.context.isExpanded
                ? `
                    height: auto;
                    overflow-y: visible;
                    transition: none 0s ease 0s;
                  `
                : ""}
              margin-top: ${props.context.isExpanded ? "0.25rem" : "0"};
            `}
          >
            <Collapse isOpen={props.context.isExpanded} transitionDuration={0}>
              {props.children}
            </Collapse>
          </div>
        </li>
      );
    },

    renderItemArrow: (props: any) => {
      const arrowProps = props.context.arrowProps || {};
      const { onClick: onArrowClick, ...restArrowProps } = arrowProps as any;

      if (!(props.item.isFolder && (props.item as any).hasChildren)) {
        return null;
      }

      const caretIcon = props.context.isExpanded
        ? "chevron-down"
        : "chevron-right";
      const isSelected = !!props.context.isSelected;

      return (
        <span
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            caretClickRef.current = true;
            requestAnimationFrame(() => {
              caretClickRef.current = false;
            });
            if (onArrowClick) {
              onArrowClick(e);
            } else if (props.context.toggleExpandedState) {
              props.context.toggleExpandedState();
            }
          }}
          {...(restArrowProps as any)}
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            width: 20px;
            height: 20px;
            margin: 6px 0 6px 6px;
            border-radius: 6px;
            color: #c5cbd3;
            cursor: pointer;
            transition: color 120ms ease;
            background-color: transparent;
            ${isSelected
              ? `
                  color: #ffffff;
                `
              : ""}
            &:hover {
              color: #ffffff;
            }

            [data-role="file"],
            [data-role="caret"] {
              position: absolute;
              inset: 0;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              transition: opacity 120ms ease;
            }

            [data-role="file"] {
              opacity: 0.8;
            }

            [data-role="caret"] {
              opacity: 0;
            }
          `}
          data-has-children="true"
          data-role="arrow"
          role="button"
          aria-label="Toggle folder"
        >
          <Icon icon="document" data-role="file" />
          <Icon icon={caretIcon} data-role="caret" />
        </span>
      );
    },

    renderDragBetweenLine: ({
      draggingPosition,
      lineProps,
    }: {
      draggingPosition: DraggingPosition & {
        linePosition?: "top" | "bottom";
      };
      lineProps: React.HTMLAttributes<HTMLDivElement>;
    }) => {
      return (
        <div
          {...lineProps}
          style={{
            position: "absolute",
            right: "0",
            top:
              draggingPosition.targetType === "between-items" &&
              draggingPosition.linePosition === "top"
                ? "0px"
                : draggingPosition.targetType === "between-items" &&
                  draggingPosition.linePosition === "bottom"
                ? "5px"
                : "-2px",
            left: `${draggingPosition.depth * 20}px`,
            height: "3px",
            backgroundColor: Colors.BLUE2,
          }}
        />
      );
    },
  };
};
